import { useState, useEffect, useRef, type CSSProperties } from 'react'
import { Coffee } from 'lucide-react'

// ============================================================================
// Launch Splash — brand intro sequence
//   1. Logo fades + scales in
//   2. Logo lifts up & shrinks a touch; cafe name wipes in left→right
//   3. Hold (read the name)
//   4. Logo + name slide up & off-screen
//   5. "Powered by OmniRise" fades in and holds, then the whole overlay
//      fades away to reveal the app.
// ============================================================================

interface LaunchSplashProps {
  onComplete: () => void
}

type Phase = -1 | 0 | 1 | 2 | 3 | 4 | 6

interface Business {
  name: string
  logo?: string
  splashEnabled: boolean
}

const EASE = 'cubic-bezier(0.16, 1, 0.3, 1)'
const SESSION_KEY = 'omnirise-splash-seen'

export function LaunchSplash({ onComplete }: LaunchSplashProps) {
  const [phase, setPhase] = useState<Phase>(-1)
  const [biz, setBiz] = useState<Business | null>(null)
  const [logoError, setLogoError] = useState(false)

  // Keep the latest onComplete without retriggering the timeline
  const onCompleteRef = useRef(onComplete)
  useEffect(() => {
    onCompleteRef.current = onComplete
  }, [onComplete])

  // Fetch just the business identity (tiny, fast)
  useEffect(() => {
    let cancelled = false
    fetch('/config/config.json')
      .then((r) => r.json())
      .then((c) => {
        if (!cancelled) {
          setBiz({
            name: c?.business?.name ?? '',
            logo: c?.business?.logo,
            splashEnabled: c?.features?.splashScreen !== false,
          })
        }
      })
      .catch(() => {
        if (!cancelled) setBiz({ name: '', splashEnabled: true })
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Lock body scroll while the splash is visible
  useEffect(() => {
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [])

  // Run the timeline once the business identity is available
  useEffect(() => {
    if (biz === null) return

    // Skip when disabled by config, or already played this browser session
    const alreadySeen =
      typeof window !== 'undefined' && sessionStorage.getItem(SESSION_KEY) === '1'

    if (!biz.splashEnabled || alreadySeen) {
      onCompleteRef.current()
      return
    }

    // Mark the splash as seen as we hand off to the app
    const finish = () => {
      try {
        sessionStorage.setItem(SESSION_KEY, '1')
      } catch {
        // ignore storage errors (private mode, etc.)
      }
      onCompleteRef.current()
    }

    const prefersReduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches

    if (prefersReduced) {
      setPhase(2)
      const done = setTimeout(finish, 800)
      return () => clearTimeout(done)
    }

    const timers: ReturnType<typeof setTimeout>[] = []
    const at = (ms: number, p: Phase) => timers.push(setTimeout(() => setPhase(p), ms))

    const raf = requestAnimationFrame(() => setPhase(0)) // logo in
    at(650, 1) // logo lifts + name wipes in
    at(1200, 2) // hold
    at(2450, 3) // slide up & off
    at(3000, 4) // powered fades in
    at(4550, 6) // overlay fades out (powered rides along, stays full opacity)
    timers.push(setTimeout(finish, 4950))

    return () => {
      cancelAnimationFrame(raf)
      timers.forEach(clearTimeout)
    }
  }, [biz])

  const name = biz?.name || ''
  const logoUrl = biz?.logo && !logoError ? biz.logo : undefined
  const groupHidden = phase >= 3

  const logoStyle: CSSProperties = {
    opacity: phase < 0 ? 0 : 1,
    transform:
      phase < 0
        ? 'scale(.9)'
        : phase >= 1
        ? 'translateY(-18px) scale(.82)'
        : 'scale(1)',
    transition: `opacity .55s ${EASE}, transform .55s ${EASE}`,
  }

  const nameStyle: CSSProperties = {
    opacity: phase >= 1 && phase < 3 ? 1 : 0,
    transform: phase >= 1 && phase < 3 ? 'translateX(0)' : 'translateX(-20px)',
    transition: `opacity .5s ${EASE} .12s, transform .5s ${EASE} .12s`,
  }

  const groupStyle: CSSProperties = {
    transform: groupHidden ? 'translateY(-130%)' : 'translateY(0)',
    opacity: groupHidden ? 0 : 1,
    transition: `transform .6s ${EASE}, opacity .6s ${EASE}`,
  }

  // Powered by stays at full opacity from phase 4 onward; the overlay fade
  // (container) carries it out — no separate fade-out for the line itself.
  const poweredStyle: CSSProperties = {
    opacity: phase >= 4 ? 1 : 0,
    transition: `opacity .45s ease`,
  }

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center overflow-hidden bg-primary"
      style={{
        opacity: phase === 6 ? 0 : 1,
        transition: `opacity .4s ease`,
      }}
      aria-live="polite"
      aria-label={name ? `${name}` : 'Loading'}
    >
      {/* Brand group: logo + name */}
      <div className="flex flex-col items-center gap-5 px-6" style={groupStyle}>
        <div className="flex items-center justify-center" style={logoStyle}>
          {logoUrl ? (
            <img
              src={logoUrl}
              alt={name}
              className="w-24 h-24 sm:w-28 sm:h-28 object-contain"
              style={{ filter: 'drop-shadow(0 12px 28px rgba(0,0,0,0.35))' }}
              onError={() => setLogoError(true)}
            />
          ) : (
            <div className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center ring-1 ring-white/20">
              <Coffee size={48} className="text-secondary" strokeWidth={1.5} />
            </div>
          )}
        </div>

        {name && (
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-secondary text-center max-w-[80vw] leading-tight">
            <span className="inline-block" style={nameStyle}>
              {name}
            </span>
          </h1>
        )}
      </div>

      {/* Powered by OmniRise */}
      <div
        className="absolute inset-0 flex items-center justify-center"
        style={poweredStyle}
      >
        <p className="text-sm tracking-wide text-white/70">
          Powered by{' '}
          <span className="font-heading font-semibold text-white tracking-normal">
            OmniRise
          </span>
        </p>
      </div>
    </div>
  )
}
